// VIDEO-EDITING — Direct HTTP client for the overlay propose + apply routes.
//
// These wrap `POST /core/video-editing/overlays/propose` and
// `POST /core/video-editing/overlays/apply`. The "Auto add hook overlays"
// panel calls these directly instead of going through Marcus chat, so the
// panel works even when the companion isn't mounted — closing the
// architectural gap that previously only `/broll/{propose,apply}` filled.
//
// All responses are Zod-validated at the boundary per frontend/CLAUDE.md
// Gotcha #2.
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

import type { Overlay } from "../types";

// The HTTP boundary stays lenient on the overlay shape — the canonical
// validator lives in the editor's own Zustand store + the renderer. The
// service is the only one who needs to verify field-level overlay sanity,
// and it already runs the strict `OverlaySchema` server-side.
const OverlayShapeSchema: z.ZodType<Overlay> = z
  .object({
    id: z.string(),
    kind: z.string(),
    text: z.string().optional(),
    role: z.string().optional(),
    anchor: z.string().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
    w: z.number().optional(),
    h: z.number().optional(),
    startSec: z.number(),
    endSec: z.number(),
  })
  .passthrough() as unknown as z.ZodType<Overlay>;

const DropReasonSchema = z.object({
  id: z.string(),
  reason: z.string(),
  duplicateOfId: z.string().optional(),
});

const ProposeResponseSchema = z.object({
  action: z.literal("video_overlay_proposals_ready"),
  projectId: z.string(),
  policy: z.string(),
  sourceActionId: z.string().nullable(),
  roles: z.array(z.string()).nullable(),
  proposalCount: z.number(),
  proposals: z.array(OverlayShapeSchema),
  wouldReplaceOverlayCount: z.number(),
  wouldReplaceOverlayIds: z.array(z.string()),
  droppedOverlayCount: z.number(),
  droppedOverlays: z.array(DropReasonSchema),
  conflicts: z.array(z.unknown()),
  durationSec: z.number().nullable(),
});

const ApplyResponseSchema = z.object({
  action: z.literal("video_overlays_upserted"),
  projectId: z.string(),
  policy: z.string(),
  sourceActionId: z.string().nullable(),
  addedOverlayCount: z.number(),
  addedOverlayIds: z.array(z.string()),
  replacedOverlayCount: z.number(),
  replacedOverlayIds: z.array(z.string()),
  repairedOverlayCount: z.number(),
  droppedOverlayCount: z.number(),
  droppedOverlays: z.array(DropReasonSchema),
  conflicts: z.array(z.unknown()),
  overlayCount: z.number(),
  overlays: z.array(OverlayShapeSchema),
});

export type ProposeOverlaysResponse = z.infer<typeof ProposeResponseSchema>;
export type ApplyOverlaysResponse = z.infer<typeof ApplyResponseSchema>;

export interface OverlayUpsertRequest {
  projectId: string;
  policy?:
    | "replaceSourceAction"
    | "append"
    | "replaceRole"
    | "replaceAll"
    | "repairOnly";
  sourceActionId?: string;
  roles?: string[];
  overlays: Overlay[];
}

/**
 * /overlays/propose generates the overlays server-side via Claude Haiku,
 * so the request only carries the project context — the payload mirrors
 * `/broll/propose` rather than the upsert/apply shape.
 */
export interface OverlayProposeRequest {
  projectId: string;
  sourceActionId?: string;
  roles?: string[];
}

async function jsonOrThrow<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      (body as { error?: string }).error ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const data: unknown = await res.json();
  return schema.parse(data);
}

export async function proposeOverlays(
  body: OverlayProposeRequest,
  signal?: AbortSignal,
): Promise<ProposeOverlaysResponse> {
  const res = await apiFetch("/core/video-editing/overlays/propose", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ProposeResponseSchema);
}

export async function applyOverlays(
  body: OverlayUpsertRequest,
  signal?: AbortSignal,
): Promise<ApplyOverlaysResponse> {
  const res = await apiFetch("/core/video-editing/overlays/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ApplyResponseSchema);
}
